import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { 
  X, MessageSquare, Send, Users, ArrowRightLeft, Plus, Trash2, 
  Clock, Calendar, RotateCcw, CheckCircle, Filter, ChevronDown
} from 'lucide-react';
import { format, parseISO, isToday, isSameDay } from 'date-fns';
import './DispatchLog.css';

const ACTION_CONFIG = {
  team_change: { icon: Users, label: 'Team Changed', color: '#2563eb' },
  route_move: { icon: ArrowRightLeft, label: 'Route Move', color: '#7c3aed' },
  job_added: { icon: Plus, label: 'Job Added', color: '#16a34a' },
  job_deleted: { icon: Trash2, label: 'Job Deleted', color: '#dc2626' },
  job_rescheduled: { icon: Calendar, label: 'Rescheduled', color: '#d97706' },
  team_assigned: { icon: Users, label: 'Team Assigned', color: '#0891b2' },
  status_change: { icon: RotateCcw, label: 'Status Change', color: '#64748b' },
  general: { icon: MessageSquare, label: 'Note', color: '#94a3b8' }
};

function DispatchLog({ isOpen, onClose, selectedDate }) {
  const { state, addDispatchComment, logDispatchActivity } = useApp();
  const { user } = useAuth();
  const [filter, setFilter] = useState('today'); // 'today' | 'all'
  const [commentText, setCommentText] = useState({});
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const bottomRef = useRef(null);
  const panelRef = useRef(null);

  // Filter and sort log entries
  const filteredEntries = useMemo(() => {
    let entries = [...(state.dispatchLog || [])];
    
    if (filter === 'today' && selectedDate) {
      // Show entries that are about today's dispatch date OR were created today
      entries = entries.filter(e => {
        const matchesJobDate = e.jobDate === selectedDate;
        const createdToday = isToday(new Date(e.createdAt));
        return matchesJobDate || createdToday;
      });
    }
    
    return entries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [state.dispatchLog, filter, selectedDate]);

  // Group entries by date
  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const dateKey = format(new Date(entry.createdAt), 'yyyy-MM-dd');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  const formatTimestamp = (ts) => {
    try {
      return format(new Date(ts), 'h:mm a');
    } catch { return ''; }
  };

  const formatDateLabel = (dateKey) => {
    try {
      const d = parseISO(dateKey);
      if (isToday(d)) return 'Today';
      return format(d, 'EEEE, MMM d');
    } catch { return dateKey; }
  };

  const handleAddComment = async (entryId) => {
    const text = commentText[entryId]?.trim();
    if (!text) return;
    await addDispatchComment(entryId, text);
    setCommentText(prev => ({ ...prev, [entryId]: '' }));
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    await logDispatchActivity({
      action: 'general',
      description: newNote.trim(),
      jobDate: selectedDate
    });
    setNewNote('');
    setShowNoteInput(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="dlog-overlay" onClick={onClose} />
      <div className="dlog-panel" ref={panelRef}>
        {/* Header */}
        <div className="dlog-header">
          <div className="dlog-header-top">
            <h2>Activity Log</h2>
            <button className="dlog-close" onClick={onClose}><X size={20} /></button>
          </div>
          <p className="dlog-subtitle">Changes, moves & manager notes</p>
          
          {/* Filter Tabs */}
          <div className="dlog-filter-tabs">
            <button 
              className={filter === 'today' ? 'active' : ''}
              onClick={() => setFilter('today')}
            >
              Today's Activity
            </button>
            <button 
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All Activity
            </button>
          </div>
        </div>

        {/* Add Note */}
        <div className="dlog-add-note">
          {showNoteInput ? (
            <div className="dlog-note-input">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a scheduling note..."
                rows={2}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <div className="dlog-note-actions">
                <button className="dlog-cancel-btn" onClick={() => { setShowNoteInput(false); setNewNote(''); }}>Cancel</button>
                <button className="dlog-send-btn" onClick={handleAddNote} disabled={!newNote.trim()}>
                  <Send size={14} /> Post
                </button>
              </div>
            </div>
          ) : (
            <button className="dlog-add-note-btn" onClick={() => setShowNoteInput(true)}>
              <MessageSquare size={16} /> Add a note...
            </button>
          )}
        </div>

        {/* Activity List */}
        <div className="dlog-body">
          {filteredEntries.length === 0 ? (
            <div className="dlog-empty">
              <Clock size={32} />
              <p>No activity yet{filter === 'today' ? ' today' : ''}</p>
              <span>Changes to routes, teams, and jobs will appear here</span>
            </div>
          ) : (
            Object.entries(groupedEntries).map(([dateKey, entries]) => (
              <div key={dateKey} className="dlog-date-group">
                <div className="dlog-date-label">{formatDateLabel(dateKey)}</div>
                {entries.map(entry => {
                  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.general;
                  const Icon = config.icon;
                  const isExpanded = expandedEntry === entry.id;
                  const commentCount = (entry.comments || []).length;

                  return (
                    <div key={entry.id} className="dlog-entry">
                      <div className="dlog-entry-main" onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}>
                        <div className="dlog-icon" style={{ background: config.color + '18', color: config.color }}>
                          <Icon size={16} />
                        </div>
                        <div className="dlog-entry-content">
                          <p className="dlog-desc">{entry.description}</p>
                          <div className="dlog-meta">
                            <span className="dlog-user">{entry.userName}</span>
                            <span className="dlog-time">{formatTimestamp(entry.createdAt)}</span>
                            {entry.jobDate && filter === 'all' && (
                              <span className="dlog-job-date">for {format(parseISO(entry.jobDate), 'MMM d')}</span>
                            )}
                          </div>
                        </div>
                        {commentCount > 0 && (
                          <span className="dlog-comment-badge">
                            <MessageSquare size={12} /> {commentCount}
                          </span>
                        )}
                      </div>

                      {/* Details & Comments */}
                      {isExpanded && (
                        <div className="dlog-expanded">
                          {/* Details */}
                          {entry.details && Object.keys(entry.details).length > 0 && (
                            <div className="dlog-details">
                              {entry.details.from && (
                                <div className="dlog-detail-row">
                                  <span className="dlog-detail-label">From:</span>
                                  <span>{entry.details.from}</span>
                                </div>
                              )}
                              {entry.details.to && (
                                <div className="dlog-detail-row">
                                  <span className="dlog-detail-label">To:</span>
                                  <span>{entry.details.to}</span>
                                </div>
                              )}
                              {entry.details.client && (
                                <div className="dlog-detail-row">
                                  <span className="dlog-detail-label">Client:</span>
                                  <span>{entry.details.client}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Comments */}
                          {(entry.comments || []).length > 0 && (
                            <div className="dlog-comments">
                              {entry.comments.map(comment => (
                                <div key={comment.id} className="dlog-comment">
                                  <strong>{comment.userName}</strong>
                                  <span className="dlog-comment-time">{formatTimestamp(comment.createdAt)}</span>
                                  <p>{comment.text}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add Comment */}
                          <div className="dlog-add-comment">
                            <input
                              type="text"
                              placeholder="Reply..."
                              value={commentText[entry.id] || ''}
                              onChange={(e) => setCommentText(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddComment(entry.id);
                              }}
                            />
                            <button 
                              onClick={() => handleAddComment(entry.id)}
                              disabled={!commentText[entry.id]?.trim()}
                            >
                              <Send size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </>
  );
}

export default DispatchLog;
